<template>
  <UModal
    :open="open"
    :dismissible="!isBusy"
    :close="!isBusy"
    class="w-full sm:max-w-lg"
    :ui="{
      overlay: 'bg-gray-950/75 dark:bg-gray-950/90 z-[60]',
      header: 'flex items-center justify-between p-3 sm:px-3 min-h-12 bg-[#e9edf3] dark:bg-(--ui-bg-elevated)',
      content: 'z-[60]',
    }"
    @update:open="handleOpenChange"
  >
    <template #header>
      <VisuallyHidden>
        <DialogTitle>{{ headerTitle }}</DialogTitle>
        <DialogDescription>{{ headerTitle }}</DialogDescription>
      </VisuallyHidden>

      <h3 class="text-base font-semibold leading-6 text-(--ui-text)">{{ headerTitle }}</h3>
    </template>
    
    <template #body>
      <div v-if="phase === 'confirm'" class="space-y-4">
        <div>
          <p class="text-xs uppercase tracking-wide text-(--ui-text-dimmed) mb-1">
            {{ t('workflows.bulkApprove.selectedReferences') }}
          </p>
          <div class="max-h-40 overflow-y-auto rounded-md border border-(--ui-border) p-2 text-sm font-mono">
            <div v-for="reference in references" :key="reference">{{ reference }}</div>
          </div>
        </div>

        <div class="flex justify-end gap-2">
          <UButton color="neutral" variant="outline" class="cursor-pointer" @click="handleOpenChange(false)">
            {{ t('workflows.bulkApprove.cancel') }}
          </UButton>
          <UButton color="primary" class="cursor-pointer" :disabled="references.length === 0" @click="start">
            {{ t('workflows.bulkApprove.approveSelected') }}
          </UButton>
        </div>
      </div>
      
      <div v-else-if="phase === 'running'" class="space-y-4">
        <p class="text-sm text-(--ui-text)">
          {{ t('workflows.bulkApprove.progress', { done: processed, total: total }) }}
        </p>
        <UProgress :model-value="processed" :max="total" />
      </div>
      
      <div v-else class="space-y-4">
        <div class="flex items-center gap-4 text-sm">
          <span class="flex items-center gap-1.5 text-success-600 dark:text-success-400">
            <UIcon name="i-lucide-circle-check" />
            {{ t('workflows.bulkApprove.succeeded', { count: succeeded.length }) }}
          </span>
          <span v-if="failed.length > 0" class="flex items-center gap-1.5 text-error-600 dark:text-error-400">
            <UIcon name="i-lucide-circle-x" />
            {{ t('workflows.bulkApprove.failed', { count: failed.length }) }}
          </span>
        </div>
        
        <div v-if="failed.length > 0" class="max-h-48 overflow-y-auto rounded-md border border-(--ui-border) divide-y divide-(--ui-border)">
          <div v-for="item in failed" :key="item.externalReference" class="p-2 text-sm">
            <span class="font-mono">{{ item.externalReference }}</span>
            <span class="text-(--ui-text-muted)"> — {{ t(reasonKey(item.status)) }}</span>
          </div>
        </div>
        
        <div class="flex justify-end">
          <UButton color="primary" class="cursor-pointer" @click="handleOpenChange(false)">
            {{ t('workflows.bulkApprove.close') }}
          </UButton>
        </div>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { DialogTitle, DialogDescription, VisuallyHidden } from 'reka-ui'
import { useBulkQuotationApproval } from '@/composables/useBulkQuotationApproval'

const props = defineProps<{
  open: boolean
  references: string[]
}>()

const emit = defineEmits<{
  (event: 'update:open', value: boolean): void
  (event: 'finished'): void
}>()

const { t } = useI18n()
const { processed, total, succeeded, failed, isRunning, reset, run } = useBulkQuotationApproval()

type Phase = 'confirm' | 'running' | 'summary'

const phase = ref<Phase>('confirm')
const isBusy = computed(() => phase.value === 'running' || isRunning.value)

const headerTitle = computed(() => phase.value === 'summary' ? t('workflows.bulkApprove.summaryTitle') : t('workflows.bulkApprove.confirmTitle'))

function reasonKey(status?: number): string {
  switch (status) {
    case 403: return 'workflows.bulkApprove.reason.notAssigned'
    case 409: return 'workflows.bulkApprove.reason.stepChanged'
    case 422: return 'workflows.bulkApprove.reason.validation'
    default: return 'workflows.bulkApprove.reason.unknown'
  }
}

async function start() {
  if (phase.value !== 'confirm') return
  
  phase.value = 'running'
  
  try {
    await run([...props.references])
  } finally {
    phase.value = 'summary'
  }
}

function handleOpenChange(value: boolean) {
  if (isBusy.value) return

  if (!value) {
    const ranToCompletion = phase.value === 'summary'
    
    emit('update:open', false)

    if (ranToCompletion) emit('finished')
    
    return
  }

  emit('update:open', true)
}

watch(() => props.open, isOpen => {
  if (isOpen && !isBusy.value) {
    reset()
    
    phase.value = 'confirm'
  }
})
</script>
